import { useMemo } from "react";
import { styled } from "@pigment-css/react";
import { ArcElement, Chart as ChartJS, Tooltip, ChartData, ChartArea, TooltipItem } from "chart.js";
import { Doughnut } from "react-chartjs-2";

interface CenterTextPluginOptions {
  centerText: string;
  centerSubtext?: string;
}

interface ChartWithArea {
  ctx: CanvasRenderingContext2D;
  chartArea: ChartArea;
}

ChartJS.register(ArcElement, Tooltip);

const ChartWrapper = styled("div")(() => ({
  position: "relative",
  height: "180px",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

export interface DonutChartProps {
  labels: string[];
  values: number[];
  colors: string[];
  centerText?: string;
  centerSubtext?: string;
  height?: number;
  cutoutPercentage?: string;
  tooltipFormatter?: (value: number, index: number) => string | string[];
}

export const DonutChart = ({
  labels,
  values,
  colors,
  centerText = "",
  centerSubtext = "",
  height = 180,
  cutoutPercentage = "72%",
  tooltipFormatter,
}: DonutChartProps) => {
  // Create plugin inside component so font size changes are picked up
  const centerTextPlugin = useMemo(
    () => ({
      id: "centerText",
      afterDatasetsDraw(chart: ChartWithArea, _args: unknown, options: CenterTextPluginOptions) {
        if (!options?.centerText) return;

        const {
          ctx,
          chartArea: { top, bottom, left, right },
        } = chart;
        ctx.save();

        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        // Draw main center text
        ctx.font = '600 16px "Alliance No.2", -apple-system, sans-serif';
        ctx.fillStyle = "#212226";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        if (options.centerSubtext) {
          // If we have subtext, adjust positioning
          ctx.fillText(options.centerText, centerX, centerY - 10);

          // Draw subtext below
          ctx.font = '400 14px "Alliance No.2", -apple-system, sans-serif';
          ctx.fillStyle = "#21222680";
          ctx.fillText(options.centerSubtext, centerX, centerY + 10);
        } else {
          // No subtext, center the main text
          ctx.fillText(options.centerText, centerX, centerY);
        }

        ctx.restore();
      },
    }),
    [],
  );

  // Register plugin dynamically
  ChartJS.register(centerTextPlugin);

  const chartData: ChartData<"doughnut"> = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderRadius: 4,
        borderWidth: 0,
        spacing: 15,
      },
    ],
  };

  const options = {
    cutout: cutoutPercentage,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#212226",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        padding: 12,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context: TooltipItem<"doughnut">) => {
            if (tooltipFormatter) {
              return tooltipFormatter(context.parsed, context.dataIndex);
            }
            return context.parsed.toString();
          },
        },
      },
      centerText: {
        centerText,
        centerSubtext,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <ChartWrapper style={{ height }}>
      <Doughnut data={chartData} options={options} />
    </ChartWrapper>
  );
};
